import React, { memo, useEffect, useState, useCallback } from "react";
import "./Footer.css";
import {
  FaFacebookF,
  FaXTwitter,
  FaInstagram,
  FaPinterestP,
  FaChevronDown,
  FaChevronRight,
  FaChevronUp
} from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { getCategories, getSubCategories } from "../pages/api/index"; 
import Currency from "./Currency";
import { useCurrency } from "../Context/CurrencyContext";

const Footer = () => {
  const navigate = useNavigate();
  
  // --- Use Currency Context instead of local state ---
  const { 
    selectedCountry, 
    setCurrencyOpen,
    currencyOpen
  } = useCurrency();
  
  // Default fallback categories
  const defaultCategories = [
    { 
      id: "footer-default-1", 
      name: "Bath & Body", 
      slug: "bath-body",
      subcategories: []
    },
    { 
      id: "footer-default-2", 
      name: "Body Care", 
      slug: "body-care",
      subcategories: []
    },
    { 
      id: "footer-default-3", 
      name: "Face Care", 
      slug: "face-care",
      subcategories: []
    },
    { 
      id: "footer-default-4", 
      name: "Hair Care", 
      slug: "hair-care",
      subcategories: []
    },
    { 
      id: "footer-default-5", 
      name: "Hand Care", 
      slug: "hand-care",
      subcategories: []
    },
    { 
      id: "footer-default-6", 
      name: "Essential Oils", 
      slug: "essential-oils",
      subcategories: []
    }
  ];
  
  const [categories, setCategories] = useState(defaultCategories);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // --- Currency handler - EXACT SAME AS HEADER ---
  const handleCurrencyClick = () => setCurrencyOpen(true);
  
  // Function to scroll to top
  const scrollToTop = useCallback(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'instant'
    });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);
  
  // Function to handle link clicks - navigate and scroll to top
  const handleLinkClick = useCallback((e, path) => {
    e.preventDefault();
    scrollToTop();
    navigate(path);
  }, [navigate, scrollToTop]);
  
  // NEW: Handle category click – goes to products filtered by category
  const handleCategoryClick = useCallback((e, category) => {
    e.preventDefault();
    scrollToTop();
    navigate(`/products?category=${category.slug}`);
  }, [navigate, scrollToTop]);

  // NEW: Handle subcategory click – goes to products filtered by category + subcategory
  const handleSubcategoryClick = useCallback((e, subcat, parentCategory) => {
    e.preventDefault();
    scrollToTop();
    navigate(`/products?category=${parentCategory.slug}&subcategory=${subcat.slug}`);
  }, [navigate, scrollToTop]);
  
  // Function to toggle category expansion
  const toggleCategory = useCallback((categoryId, e) => {
    if (e) e.preventDefault();
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  }, []);

  // Extract array data from API response
  const extractArrayData = (data, possibleKeys = ['data', 'categories', 'subcategories']) => {
    if (Array.isArray(data)) return data;
    
    for (let key of possibleKeys) {
      if (data && Array.isArray(data[key])) {
        return data[key];
      }
    }
    
    return [];
  };
  
  // Process category data to extract categories and subcategories
  const processCategoriesData = (categoriesData, subcategoriesData = []) => {
    if (!categoriesData || categoriesData.length === 0) {
      return defaultCategories;
    }
    
    const processed = [];
    const mainCategoriesMap = {};
    
    // First pass: collect all main categories
    categoriesData.forEach((category, index) => {
      let categoryName = category.name || category.title || category.categoryName || `Category ${index + 1}`;
      let slug = category.slug || category.name?.toLowerCase().replace(/\s+/g, '-') || `category-${index + 1}`;
      let originalId = category.id || category._id || category.categoryId || slug;
      let categoryId = `footer-api-${originalId}`;
      
      const mainCategory = {
        ...category,
        id: categoryId,
        name: categoryName,
        slug: slug,
        originalId: originalId,
        isMainCategory: true,
        subcategories: []
      };
      
      mainCategoriesMap[originalId] = mainCategory;
      processed.push(mainCategory);
    });
    
    // Second pass: assign subcategories to their parent categories
    if (subcategoriesData && subcategoriesData.length > 0) {
      subcategoriesData.forEach((subcat) => {
        const parentId = subcat.parentId || subcat.categoryId || subcat.parentCategoryId;
        
        if (parentId && mainCategoriesMap[parentId]) {
          let subcatName = subcat.name || subcat.title || subcat.subcategoryName || "Subcategory";
          let subcatSlug = subcat.slug || subcat.name?.toLowerCase().replace(/\s+/g, '-') || "subcategory";
          let originalSubId = subcat.id || subcat._id || subcat.subcategoryId || subcatSlug;
          let subcatId = `footer-api-sub-${originalSubId}`;
          
          mainCategoriesMap[parentId].subcategories.push({
            ...subcat,
            id: subcatId,
            name: subcatName,
            slug: subcatSlug,
            originalId: originalSubId,
            parentId: parentId,
            isSubcategory: true
          });
        }
      });
    }
    
    // Also check for nested subcategories within categories data
    categoriesData.forEach((category) => {
      let originalId = category.id || category._id || category.categoryId || category.slug;
      
      const subcategoryFields = ['subcategories', 'children', 'child_categories', 'sub_categories'];
      
      subcategoryFields.forEach(field => {
        if (category[field] && Array.isArray(category[field]) && category[field].length > 0) {
          category[field].forEach((subcat, subIndex) => {
            let subcatName = subcat.name || subcat.title || subcat.subcategoryName || `Subcategory ${subIndex + 1}`;
            let subcatSlug = subcat.slug || subcat.name?.toLowerCase().replace(/\s+/g, '-') || `subcategory-${subIndex + 1}`;
            let originalSubId = subcat.id || subcat._id || subcat.subcategoryId || subcatSlug;
            let subcatId = `footer-api-sub-${originalSubId}`;
            
            if (mainCategoriesMap[originalId]) {
              const existingSubcatIndex = mainCategoriesMap[originalId].subcategories.findIndex(
                s => s.originalId === originalSubId
              );
              
              if (existingSubcatIndex === -1) {
                mainCategoriesMap[originalId].subcategories.push({
                  ...subcat,
                  id: subcatId,
                  name: subcatName,
                  slug: subcatSlug,
                  originalId: originalSubId,
                  parentId: originalId,
                  isSubcategory: true
                });
              }
            }
          });
        }
      });
    });
    
    return processed;
  };
  
  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      
      console.log("Fetching categories and subcategories for footer...");
      
      let categoriesData = [];
      let subcategoriesData = [];
      
      try {
        const [categoriesResponse, subcategoriesResponse] = await Promise.all([
          getCategories(),
          getSubCategories()
        ]);

        console.log("Categories API Response:", categoriesResponse);
        console.log("Subcategories API Response:", subcategoriesResponse);

        categoriesData = extractArrayData(categoriesResponse, ['data', 'categories']);
        subcategoriesData = extractArrayData(subcategoriesResponse, ['data', 'subcategories']);

        if (!categoriesData || categoriesData.length === 0) {
          console.log("No categories found in API response");
        }
        if (!subcategoriesData || subcategoriesData.length === 0) {
          console.log("No subcategories found in API response");
        }

      } catch (error) {
        console.error("Error fetching categories or subcategories:", error);
      }

      if ((!categoriesData || categoriesData.length === 0) && 
          (!subcategoriesData || subcategoriesData.length === 0)) {
        console.log("Both categories and subcategories APIs failed, using default categories");
        setCategories(defaultCategories);
        setLoadingCategories(false);
        return;
      }
      
      const processedCategories = processCategoriesData(categoriesData || [], subcategoriesData || []);
      const footerCategories = processedCategories.slice(0, 8);
      
      console.log("Processed footer categories:", footerCategories);
      setCategories(footerCategories);
      setLoadingCategories(false);
      
    } catch (error) {
      console.error("Error in fetchCategories:", error);
      setCategories(defaultCategories);
      setLoadingCategories(false);
    }
  };
  
  useEffect(() => {
    fetchCategories();
    
    const timeoutId = setTimeout(() => {
      if (loadingCategories) {
        console.log("Categories fetch timeout, using available data");
        setLoadingCategories(false);
      }
    }, 8000);
    
    return () => clearTimeout(timeoutId);
  }, []);
  
  // Render category with dropdown functionality
  const renderCategoryWithDropdown = (category) => {
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    const isExpanded = expandedCategories[category.id];
    
    return (
      <div key={category.id} className="footer-category-item">
        <div 
          className={`footer-category-header ${hasSubcategories ? 'has-subcategories' : ''}`}
          onClick={(e) => hasSubcategories && toggleCategory(category.id, e)}
          style={{ cursor: hasSubcategories ? 'pointer' : 'default' }}
        >
          <span
            className="footer-category-text footer-link-hover"
            onClick={(e) => {
              if (!hasSubcategories) {
                // No subcategories – navigate to category products directly
                handleCategoryClick(e, category);
              }
            }}
          >
            {category.name}
          </span>

          {hasSubcategories && (
            <span className="footer-category-toggle">
              {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
            </span>
          )}
        </div>
        
        {hasSubcategories && isExpanded && (
          <ul className="footer-subcategories-list">
            {category.subcategories.map((subcat) => (
              <li key={subcat.id}>
                <div 
                  className="footer-subcategory-link"
                  style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <FaChevronRight size={10} className="footer-subcategory-arrow" />
                  <span 
                    className="footer-subcategory-text"
                    onClick={(e) => handleSubcategoryClick(e, subcat, category)}
                  >
                    {subcat.name}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  // =============== FIXED: Helper function to get flag image URL ===============
  const getFlagImageUrl = (country) => {
    if (!country) return "/images/flags/default.svg";
    
    // Try API-provided flag fields
    const flagField = country.flag || country.flagUrl || country.image || country.countryFlag;
    if (flagField && typeof flagField === 'string' && flagField.trim() !== '') {
      return flagField; // will be used as src; onError will handle if broken
    }
    
    // Fallback to flagcdn using two-letter country code
    if (country.countryCode) {
      return `https://flagcdn.com/w320/${country.countryCode.toLowerCase()}.png`;
    }
    if (country.code) {
      return `https://flagcdn.com/w320/${country.code.toLowerCase()}.png`;
    }
    
    return "/images/flags/default.svg";
  };
  // =============== End of fix ===============

  return (
    <>
      <footer className="footer" id="main-footer">
        <div className="footer-wrapper">
          {/* ================= TOP SECTION ================= */}
          <div className="footer-top">
            <div className="footer-col footer-col-categories">
              <h4>Categories</h4>
              <div className="footer-categories-list">
                {loadingCategories ? (
                  [1, 2, 3, 4, 5, 6].map((n) => (
                    <div key={`footer-loading-${n}`} className="footer-category-item">
                      <div className="footer-category-header">
                        <span className="footer-link-hover" style={{ opacity: 0.7, fontStyle: 'italic' }}>
                          Loading category {n}...
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <>
                    {categories.map((category) => renderCategoryWithDropdown(category))}
                    {/* FIXED: Sale link now goes to /products (product list page) */}
                    <div className="footer-category-item">
                      <div className="footer-category-header">
                        <span
                          className="footer-category-text footer-link-hover"
                          onClick={(e) => handleLinkClick(e, "/products")}
                          style={{ cursor: 'pointer' }}
                        >
                          Sale %
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="footer-col">
              <h4>Customer Service</h4>
              <ul>
                <li key="footer-shipping">
                  <div 
                    className="footer-link-hover" 
                    onClick={(e) => handleLinkClick(e, "/shipping-policy")}
                    style={{ cursor: 'pointer' }}
                  >
                    Shipping
                  </div>
                </li>
                <li key="footer-contact">
                  <div 
                    className="footer-link-hover" 
                    onClick={(e) => handleLinkClick(e, "/contact")}
                    style={{ cursor: 'pointer' }}
                  >
                    Contact Us
                  </div>
                </li>
                <li key="footer-phone">
                  <a href="tel:+919819583700" className="footer-link-hover">
                    +91 98195 83700
                  </a>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>Information</h4>
              <ul>
                <li key="footer-returns">
                  <div 
                    className="footer-link-hover" 
                    onClick={(e) => handleLinkClick(e, "/return-refund")}
                    style={{ cursor: 'pointer' }}
                  >
                    Return and Refunds
                  </div>
                </li>
                <li key="footer-legal">
                  <div 
                    className="footer-link-hover" 
                    onClick={(e) => handleLinkClick(e, "/legal")}
                    style={{ cursor: 'pointer' }}
                  >
                    Legal Area
                  </div>
                </li>
                <li key="footer-about">
                  <div 
                    className="footer-link-hover" 
                    onClick={(e) => handleLinkClick(e, "/about")}
                    style={{ cursor: 'pointer' }}
                  >
                    About Us
                  </div>
                </li>
                <li key="footer-address" className="footer-address-info">
                  <div className="footer-business-name">Volumaire Health And Beauty Pvt Ltd.</div>
                  <div className="footer-registration-address">
                    701, Maitri Marigold, Maitri Gardens,<br />
                    Pokhran Road No. 2,<br />
                    Thane West – 400601
                  </div>
                </li>
              </ul>
            </div>

            <div className="footer-col">
              <h4>About Us</h4>
              <div className="footer-about-text">
                We could not have created this demo without the help of an amazing source of content and products. Visit our about page to find out where all the products used in this demo come from.{' '}
                <span
                  className="footer-about-link"
                  onClick={(e) => handleLinkClick(e, "/about")}
                >
                  <u>About us</u> 
                </span>
              </div>
            </div>
          </div>

          <div className="footer-middle">
            <div className="footer-left-group">
              {/* =============== FIXED: Currency display with flag and currency code =============== */}
              <div 
                className="footer-currency-display" 
                onClick={handleCurrencyClick}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src={getFlagImageUrl(selectedCountry)} 
                  alt={selectedCountry?.countryName || selectedCountry?.countryCode || 'Currency'}
                  className="footer-currency-flag"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/images/flags/default.svg";
                  }}
                />
                <span className="footer-currency-code">{selectedCountry?.currency?.code}</span>
                <FaChevronDown className="footer-currency-arrow" />
              </div>

              <div className="social-icons">
                <a href="#" className="footer-link-hover" key="footer-fb"><FaFacebookF /></a>
                <a href="#" className="footer-link-hover" key="footer-twitter"><FaXTwitter /></a>
                <a href="#" className="footer-link-hover" key="footer-instagram"><FaInstagram /></a>
                <a href="#" className="footer-link-hover" key="footer-pinterest"><FaPinterestP /></a>
              </div>
            </div>
          </div>

          <div className="footer-brand">Volumaire</div>
        </div>
      </footer>

      {/* --- CURRENCY DIALOG - EXACT SAME AS HEADER --- */}
      <Currency 
        open={currencyOpen} 
        onClose={() => setCurrencyOpen(false)} 
      />
    </>
  );
};

export default memo(Footer);